"use strict";
/**
 * @module botframework-streaming
 */
/**
 * Copyright (c) Microsoft Corporation. All rights reserved.
 * Licensed under the MIT License.
 */
var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
var __generator = (this && this.__generator) || function (thisArg, body) {
    var _ = { label: 0, sent: function() { if (t[0] & 1) throw t[1]; return t[1]; }, trys: [], ops: [] }, f, y, t, g;
    return g = { next: verb(0), "throw": verb(1), "return": verb(2) }, typeof Symbol === "function" && (g[Symbol.iterator] = function() { return this; }), g;
    function verb(n) { return function (v) { return step([n, v]); }; }
    function step(op) {
        if (f) throw new TypeError("Generator is already executing.");
        while (_) try {
            if (f = 1, y && (t = op[0] & 2 ? y["return"] : op[0] ? y["throw"] || ((t = y["return"]) && t.call(y), 0) : y.next) && !(t = t.call(y, op[1])).done) return t;
            if (y = 0, t) op = [op[0] & 2, t.value];
            switch (op[0]) {
                case 0: case 1: t = op; break;
                case 4: _.label++; return { value: op[1], done: false };
                case 5: _.label++; y = op[1]; op = [0]; continue;
                case 7: op = _.ops.pop(); _.trys.pop(); continue;
                default:
                    if (!(t = _.trys, t = t.length > 0 && t[t.length - 1]) && (op[0] === 6 || op[0] === 2)) { _ = 0; continue; }
                    if (op[0] === 3 && (!t || (op[1] > t[0] && op[1] < t[3]))) { _.label = op[1]; break; }
                    if (op[0] === 6 && _.label < t[1]) { _.label = t[1]; t = op; break; }
                    if (t && _.label < t[2]) { _.label = t[2]; _.ops.push(op); break; }
                    if (t[2]) _.ops.pop();
                    _.trys.pop(); continue;
            }
            op = body.call(thisArg, _);
        } catch (e) { op = [6, e]; y = 0; } finally { f = t = 0; }
        if (op[0] & 5) throw op[1]; return { value: op[0] ? op[1] : void 0, done: true };
    }
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.ContentStream = void 0;
/**
 * A stream of fixed or infinite length containing content to be decoded.
 */
var ContentStream = /** @class */ (function () {
    /**
     * Initializes a new instance of the [ContentStream](xref:botframework-streaming.ContentStream) class.
     *
     * @param id The ID assigned to this instance.
     * @param assembler The [PayloadAssembler](xref:botframework-streaming.PayloadAssembler) assigned to this instance.
     */
    function ContentStream(id, assembler) {
        if (!assembler) {
            throw Error('Null Argument Exception');
        }
        this.id = id;
        this.assembler = assembler;
    }
    Object.defineProperty(ContentStream.prototype, "contentType", {
        /**
         * Gets the name of the type of the object contained within this [ContentStream](xref:botframework-streaming.ContentStream).
         *
         * @returns The [PayloadType](xref:botframework-streaming.PayloadType) of this [ContentStream](xref:botframework-streaming.ContentStream).
         */
        get: function () {
            return this.assembler.payloadType;
        },
        enumerable: false,
        configurable: true
    });
    Object.defineProperty(ContentStream.prototype, "length", {
        /**
         * Gets the length of this [ContentStream](xref:botframework-streaming.ContentStream).
         *
         * @returns A number representing the length of this [ContentStream](xref:botframework-streaming.ContentStream).
         */
        get: function () {
            return this.assembler.contentLength;
        },
        enumerable: false,
        configurable: true
    });
    /**
     * Gets the data contained within this [ContentStream](xref:botframework-streaming.ContentStream).
     *
     * @returns This [ContentStream's](xref:botframework-streaming.ContentStream) [SubscribableStream](xref:botframework-streaming.SubscribableStream).
     */
    ContentStream.prototype.getStream = function () {
        if (!this.stream) {
            this.stream = this.assembler.getPayloadStream();
        }
        return this.stream;
    };
    /**
     * Closes the assembler.
     */
    ContentStream.prototype.cancel = function () {
        this.assembler.close();
    };
    /**
     * Gets the [SubscribableStream](xref:botframework-streaming.SubscribableStream) content as a string.
     *
     * @returns A string Promise with [SubscribableStream](xref:botframework-streaming.SubscribableStream) content.
     */
    ContentStream.prototype.readAsString = function () {
        return __awaiter(this, void 0, void 0, function () {
            var bufferArray;
            return __generator(this, function (_a) {
                switch (_a.label) {
                    case 0: return [4 /*yield*/, this.readAll()];
                    case 1:
                        bufferArray = (_a.sent()).bufferArray;
                        return [2 /*return*/, (bufferArray || []).map(function (result) { return result.toString('utf8'); }).join('')];
                }
            });
        });
    };
    /**
     * Gets the [SubscribableStream](xref:botframework-streaming.SubscribableStream) content as a typed JSON object.
     *
     * @returns A typed object Promise with `SubscribableStream` content.
     */
    ContentStream.prototype.readAsJson = function () {
        return __awaiter(this, void 0, void 0, function () {
            var stringToParse;
            return __generator(this, function (_a) {
                switch (_a.label) {
                    case 0: return [4 /*yield*/, this.readAsString()];
                    case 1:
                        stringToParse = _a.sent();
                        return [2 /*return*/, JSON.parse(stringToParse)];
                }
            });
        });
    };
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    ContentStream.prototype.readAll = function () {
        return __awaiter(this, void 0, void 0, function () {
            var allData, count, stream, chunk, readToEnd;
            var _this = this;
            return __generator(this, function (_a) {
                switch (_a.label) {
                    case 0:
                        allData = [];
                        count = 0;
                        stream = this.getStream();
                        // populate the array with any existing buffers
                        while (count < stream.length) {
                            chunk = stream.read(stream.length);
                            allData.push(chunk);
                            count += chunk.length;
                        }
                        if (!(count < this.length)) return [3 /*break*/, 2];
                        readToEnd = new Promise(function (resolve) {
                            // eslint-disable-next-line @typescript-eslint/no-explicit-any
                            var callback = function (cs) { return function (chunk) {
                                allData.push(chunk);
                                count += chunk.length;
                                if (count === cs.length) {
                                    resolve(true);
                                }
                            }; };
                            stream.subscribe(callback(_this));
                        });
                        return [4 /*yield*/, readToEnd];
                    case 1:
                        _a.sent();
                        _a.label = 2;
                    case 2: return [2 /*return*/, { bufferArray: allData, size: count }];
                }
            });
        });
    };
    return ContentStream;
}());
exports.ContentStream = ContentStream;
//# sourceMappingURL=contentStream.js.map